/**
 * Iter-334 Phase A — Vendor Marketplace Dashboard.
 * URL: /vendor-dashboard/marketplace
 *
 * Single-page dashboard with tabs:
 *  - Overview (KPIs, publish CTA, Connect status)
 *  - Profile
 *  - Packages
 *  - Portfolio (media manager + cover selection)
 *  - Availability
 *  - Bookings
 *  - Share
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Upload, Trash2, ExternalLink, Copy, Star, CalendarDays, Plus,
  CheckCircle2, AlertTriangle, DollarSign, ImageIcon, Settings, LinkIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import LinkedAccountsPanel from '../../components/LinkedAccountsPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TABS = [
  { key: 'overview', label: 'Overview', icon: DollarSign },
  { key: 'profile', label: 'Profile', icon: Settings },
  { key: 'packages', label: 'Packages', icon: Star },
  { key: 'portfolio', label: 'Portfolio', icon: ImageIcon },
  { key: 'availability', label: 'Availability', icon: CalendarDays },
  { key: 'bookings', label: 'Bookings', icon: CheckCircle2 },
  { key: 'linked-accounts', label: 'Linked accounts', icon: LinkIcon },
  { key: 'share', label: 'Share', icon: LinkIcon },
];

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function VendorDashboardMarketplace() {
  const [params, setParams] = useSearchParams();
  const activeTab = params.get('tab') || 'overview';
  const setTab = (k) => setParams({ tab: k });

  const [profile, setProfile] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/vendor/me/marketplace/profile`, AUTH()),
        axios.get(`${API}/marketplace/categories`),
      ]);
      setProfile(p.data);
      setCats(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe Connect return
  useEffect(() => {
    if (params.get('connect') === 'return') {
      axios.get(`${API}/vendor/me/marketplace/stripe-connect/status`, AUTH())
        .then(() => { load(); toast.success('Stripe Connect updated'); })
        .catch(() => {});
    }
  }, [params, load]);

  if (loading) return <div className="min-h-[80vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;

  if (!profile?.onboarded) return <OnboardForm cats={cats} onDone={load} />;

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs text-slate-500">Vendor dashboard</div>
              <h1 className="text-xl font-bold" data-testid="vendor-dash-name">{profile.company_name}</h1>
            </div>
            <div className="flex items-center gap-2">
              {profile.is_published && profile.admin_approved && (
                <Link to={`/vendor/${profile.slug}`} target="_blank" className="text-xs text-teal-700 hover:underline inline-flex items-center gap-1">
                  View public page <ExternalLink className="w-3 h-3" />
                </Link>
              )}
              <PublishBadge profile={profile} />
            </div>
          </div>
          <div className="flex gap-1 mt-4 overflow-x-auto -mb-2 pb-1">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`whitespace-nowrap px-3 py-2 text-sm rounded-md flex items-center gap-2 ${activeTab === t.key ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid={`vdash-tab-${t.key}`}
              ><t.icon className="w-3.5 h-3.5" />{t.label}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {activeTab === 'overview'     && <OverviewTab profile={profile} reload={load} />}
        {activeTab === 'profile'      && <ProfileTab profile={profile} cats={cats} reload={load} />}
        {activeTab === 'packages'     && <PackagesTab />}
        {activeTab === 'portfolio'    && <PortfolioTab reload={load} />}
        {activeTab === 'availability' && <AvailabilityTab />}
        {activeTab === 'bookings'     && <BookingsTab />}
        {activeTab === 'linked-accounts' && <LinkedAccountsPanel />}
        {activeTab === 'share'        && <ShareTab slug={profile.slug} />}
      </main>
    </div>
  );
}

function PublishBadge({ profile }) {
  if (profile.is_published && profile.admin_approved) {
    return <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid="publish-badge-live">LIVE</span>;
  }
  if (profile.is_published && !profile.admin_approved) {
    return <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">Pending approval</span>;
  }
  return <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">Draft</span>;
}

// ─── Onboarding form ───────────────────────────────────────────────
function OnboardForm({ cats, onDone }) {
  const [f, setF] = useState({ company_name: '', category_slug: '', tagline: '', city: '', state: '', contact_email: '', contact_phone: '', accept_terms: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.accept_terms) { toast.error('Please accept the marketplace terms.'); return; }
    if (!f.company_name || !f.category_slug) { toast.error('Company name and category required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/vendor/me/marketplace/onboard`, f, AUTH());
      toast.success('Welcome to the marketplace!');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboarding failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <Card className="w-full max-w-lg p-6 space-y-3" data-testid="vendor-onboard-form">
        <h1 className="text-2xl font-bold">List your business on iEvntür</h1>
        <p className="text-sm text-slate-600">Set up your marketplace profile in under 2 minutes.</p>
        <div><Label>Company name *</Label><Input value={f.company_name} onChange={e => setF({ ...f, company_name: e.target.value })} data-testid="onboard-name" /></div>
        <div>
          <Label>Category *</Label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={f.category_slug} onChange={e => setF({ ...f, category_slug: e.target.value })} data-testid="onboard-category">
            <option value="">Select…</option>
            {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
        <div><Label>Tagline</Label><Input value={f.tagline} onChange={e => setF({ ...f, tagline: e.target.value })} placeholder="Award-winning wedding photography" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>City</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} /></div>
          <div><Label>State</Label><Input value={f.state} onChange={e => setF({ ...f, state: e.target.value })} /></div>
        </div>
        <div><Label>Contact email</Label><Input type="email" value={f.contact_email} onChange={e => setF({ ...f, contact_email: e.target.value })} /></div>
        <div><Label>Contact phone</Label><Input value={f.contact_phone} onChange={e => setF({ ...f, contact_phone: e.target.value })} /></div>
        <label className="flex gap-2 items-start text-sm text-slate-600 pt-2">
          <input type="checkbox" className="mt-1" checked={f.accept_terms} onChange={e => setF({ ...f, accept_terms: e.target.checked })} data-testid="onboard-terms" />
          I agree to the <Link to="/terms" target="_blank" className="text-teal-700 hover:underline">iEvntür Marketplace Vendor Terms</Link> — including a 10% platform commission on bookings.
        </label>
        <Button onClick={submit} disabled={busy} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="onboard-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create my listing'}
        </Button>
      </Card>
    </div>
  );
}

// ─── Overview tab ──────────────────────────────────────────────────
function OverviewTab({ profile, reload }) {
  const [summary, setSummary] = useState(null);
  const [connect, setConnect] = useState({ connected: false, onboarding_completed: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/vendor/me/marketplace/dashboard-summary`, AUTH()).then(r => setSummary(r.data)).catch(() => setSummary({})),
      axios.get(`${API}/vendor/me/marketplace/stripe-connect/status`, AUTH()).then(r => setConnect(r.data)).catch(() => {}),
    ]);
  }, []);

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vendor/me/marketplace/stripe-connect/onboarding-link`, { origin_url: window.location.origin }, AUTH());
      window.location.href = r.data.onboarding_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stripe onboarding failed');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/vendor/me/marketplace/publish`, {}, AUTH());
      toast.success('Submitted for admin approval');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-4 lg:col-span-2">
        <div className="text-sm text-slate-500">Booking activity</div>
        {!summary ? <div className="mt-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <Kpi label="Deposit paid" value={summary.deposit_paid || 0} />
            <Kpi label="Fully paid" value={summary.paid_in_full || 0} />
            <Kpi label="Completed" value={summary.completed || 0} />
            <Kpi label="Quote requests" value={summary.quote_requested || 0} />
            <Kpi label="Net earned" value={fmt(summary.gross_net || 0)} money />
            <Kpi label="Gross collected" value={fmt(summary.gross_paid || 0)} money />
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="text-sm text-slate-500">Setup</div>
        <div className="mt-2 space-y-2">
          <SetupStep done={true} label="Profile created" />
          <SetupStep done={connect.onboarding_completed} label="Stripe Connect payouts" cta={!connect.onboarding_completed && (
            <Button size="sm" onClick={startConnect} disabled={busy} className="mt-1 bg-teal-600 hover:bg-teal-700" data-testid="start-connect-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set up payouts'}
            </Button>
          )} />
          <SetupStep done={profile.is_published && profile.admin_approved} label="Published on marketplace" cta={!profile.is_published && (
            <Button size="sm" onClick={publish} disabled={busy || !connect.onboarding_completed} className="mt-1 bg-teal-600 hover:bg-teal-700" data-testid="publish-btn">
              Submit for approval
            </Button>
          )} />
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, money }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`font-bold ${money ? 'text-emerald-700' : 'text-slate-900'} text-lg`}>{value}</div>
    </div>
  );
}

function SetupStep({ done, label, cta }) {
  return (
    <div className="flex items-start gap-2">
      {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${done ? 'text-slate-600' : 'font-medium text-slate-900'}`}>{label}</div>
        {cta}
      </div>
    </div>
  );
}

// ─── Profile tab ──────────────────────────────────────────────────
function ProfileTab({ profile, reload }) {
  const [f, setF] = useState({
    tagline: profile.tagline || '', bio_html: profile.bio_html || '',
    city: profile.city || '', state: profile.state || '',
    logo_url: profile.logo_url || '', website_url: profile.website_url || '',
    instagram: profile.instagram || '', tiktok: profile.tiktok || '', facebook: profile.facebook || '',
    contact_email: profile.contact_email || '', contact_phone: profile.contact_phone || '',
    cancellation_policy: profile.cancellation_policy || '',
    brand_accent_color: profile.brand_accent_color || '',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/vendor/me/marketplace/profile`, f, AUTH());
      toast.success('Profile saved');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 space-y-3">
      <div><Label>Tagline</Label><Input value={f.tagline} onChange={e => setF({ ...f, tagline: e.target.value })} data-testid="profile-tagline" /></div>
      <div><Label>About (supports basic HTML)</Label><Textarea rows={6} value={f.bio_html} onChange={e => setF({ ...f, bio_html: e.target.value })} data-testid="profile-bio" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>City</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} /></div>
        <div><Label>State</Label><Input value={f.state} onChange={e => setF({ ...f, state: e.target.value })} /></div>
      </div>
      <div><Label>Logo URL</Label><Input value={f.logo_url} onChange={e => setF({ ...f, logo_url: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Contact email</Label><Input value={f.contact_email} onChange={e => setF({ ...f, contact_email: e.target.value })} /></div>
        <div><Label>Contact phone</Label><Input value={f.contact_phone} onChange={e => setF({ ...f, contact_phone: e.target.value })} /></div>
      </div>
      <div><Label>Website</Label><Input value={f.website_url} onChange={e => setF({ ...f, website_url: e.target.value })} /></div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label>Instagram</Label><Input value={f.instagram} onChange={e => setF({ ...f, instagram: e.target.value })} placeholder="@handle" /></div>
        <div><Label>TikTok</Label><Input value={f.tiktok} onChange={e => setF({ ...f, tiktok: e.target.value })} /></div>
        <div><Label>Facebook</Label><Input value={f.facebook} onChange={e => setF({ ...f, facebook: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Brand accent color</Label>
          <div className="flex gap-2 items-center">
            <input type="color" className="w-10 h-10 rounded" value={f.brand_accent_color || '#0f766e'} onChange={e => setF({ ...f, brand_accent_color: e.target.value })} />
            <Input value={f.brand_accent_color} onChange={e => setF({ ...f, brand_accent_color: e.target.value })} placeholder="#0f766e" />
          </div>
        </div>
        <div><Label>Cancellation policy</Label><Textarea rows={3} value={f.cancellation_policy} onChange={e => setF({ ...f, cancellation_policy: e.target.value })} /></div>
      </div>
      <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="save-profile-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save profile'}
      </Button>
    </Card>
  );
}

// ─── Packages tab ─────────────────────────────────────────────────
function PackagesTab() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await axios.get(`${API}/vendor/me/marketplace/packages`, AUTH());
    setItems(r.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between mb-3">
        <h2 className="font-semibold">Packages ({items.length})</h2>
        <Button size="sm" onClick={() => setShowNew(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="new-package-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New package</Button>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(p => <PackageCard key={p.id} pkg={p} reload={load} />)}
          {items.length === 0 && <Card className="p-6 text-center text-slate-500 md:col-span-2">No packages yet. Add one to accept instant bookings.</Card>}
        </div>
      )}
      {showNew && <PackageEditor onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function PackageCard({ pkg, reload }) {
  const [editing, setEditing] = useState(false);
  const del = async () => {
    if (!window.confirm(`Deactivate "${pkg.title}"?`)) return;
    await axios.delete(`${API}/vendor/me/marketplace/packages/${pkg.id}`, AUTH());
    toast.success('Package deactivated'); reload();
  };
  return (
    <Card className="p-4 relative" data-testid={`vdash-pkg-${pkg.id}`}>
      <div className="flex justify-between">
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{pkg.title}</div>
          {pkg.description && <div className="text-sm text-slate-600 mt-1 line-clamp-2">{pkg.description}</div>}
        </div>
        <div className="text-right ml-2">
          <div className="text-xl font-bold text-teal-700">{fmt(pkg.base_price)}</div>
          <div className="text-xs text-slate-500">{pkg.booking_type === 'instant' ? 'Instant Book' : 'Quote'}</div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
        <Button size="sm" variant="outline" className="text-red-600" onClick={del}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
      {editing && <PackageEditor pkg={pkg} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); reload(); }} />}
    </Card>
  );
}

function PackageEditor({ pkg, onClose, onSaved }) {
  const [f, setF] = useState({
    title: pkg?.title || '', description: pkg?.description || '',
    base_price: pkg?.base_price || 0, duration_minutes: pkg?.duration_minutes || 60,
    deposit_type: pkg?.deposit_type || 'percent', deposit_value: pkg?.deposit_value || 30,
    booking_type: pkg?.booking_type || 'instant',
    allows_partial_payments: pkg ? pkg.allows_partial_payments : true,
    inclusions: (pkg?.inclusions || []).join('\n'),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const payload = {
      ...f, base_price: parseFloat(f.base_price), duration_minutes: parseInt(f.duration_minutes || 0),
      deposit_value: parseFloat(f.deposit_value), inclusions: f.inclusions.split('\n').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (pkg) await axios.patch(`${API}/vendor/me/marketplace/packages/${pkg.id}`, payload, AUTH());
      else     await axios.post(`${API}/vendor/me/marketplace/packages`, payload, AUTH());
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="package-editor">
        <div className="font-semibold text-lg">{pkg ? 'Edit' : 'New'} package</div>
        <div><Label>Title *</Label><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="pkg-title" /></div>
        <div><Label>Description</Label><Textarea rows={3} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Base price ($)</Label><Input type="number" step="0.01" value={f.base_price} onChange={e => setF({ ...f, base_price: e.target.value })} data-testid="pkg-price" /></div>
          <div><Label>Duration (min)</Label><Input type="number" value={f.duration_minutes} onChange={e => setF({ ...f, duration_minutes: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Booking type</Label>
            <select className="w-full border rounded-md px-3 py-2 text-sm" value={f.booking_type} onChange={e => setF({ ...f, booking_type: e.target.value })} data-testid="pkg-type">
              <option value="instant">Instant Book</option>
              <option value="quote">Request a Quote</option>
            </select>
          </div>
          <div>
            <Label>Deposit</Label>
            <div className="flex gap-1">
              <select className="border rounded-md px-2 py-2 text-sm flex-1" value={f.deposit_type} onChange={e => setF({ ...f, deposit_type: e.target.value })}>
                <option value="percent">% of total</option>
                <option value="fixed">Fixed $</option>
                <option value="none">Full payment upfront</option>
              </select>
              {f.deposit_type !== 'none' && <Input type="number" step="0.01" value={f.deposit_value} onChange={e => setF({ ...f, deposit_value: e.target.value })} className="w-20" />}
            </div>
          </div>
        </div>
        <label className="flex gap-2 items-center text-sm">
          <input type="checkbox" checked={f.allows_partial_payments} onChange={e => setF({ ...f, allows_partial_payments: e.target.checked })} />
          Allow partial payments (client can pay remainder later)
        </label>
        <div><Label>What's included (one per line)</Label><Textarea rows={4} value={f.inclusions} onChange={e => setF({ ...f, inclusions: e.target.value })} /></div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="pkg-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ─── Portfolio tab ────────────────────────────────────────────────
function PortfolioTab({ reload }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/vendor/me/marketplace/media`, AUTH());
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!url) return;
    setBusy(true);
    try {
      await axios.post(`${API}/vendor/me/marketplace/media`, { url, media_type: 'image' }, AUTH());
      setUrl(''); toast.success('Media added');
      load(); if (reload) reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
    finally { setBusy(false); }
  };
  const setCover = async (id) => {
    await axios.post(`${API}/vendor/me/marketplace/media/${id}/set-cover`, {}, AUTH());
    toast.success('Cover updated'); if (reload) reload();
  };
  const del = async (id) => {
    if (!window.confirm('Remove this media?')) return;
    await axios.delete(`${API}/vendor/me/marketplace/media/${id}`, AUTH());
    load(); if (reload) reload();
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <Label>Add image URL</Label>
        <div className="flex gap-2 mt-1">
          <Input placeholder="https://…" value={url} onChange={e => setUrl(e.target.value)} data-testid="media-url" />
          <Button onClick={add} disabled={busy || !url} className="bg-teal-600 hover:bg-teal-700" data-testid="media-add-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
        <div className="text-xs text-slate-500 mt-1">Auto-cover picks the largest landscape image. Set a cover manually if you prefer.</div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {items.map(m => (
          <div key={m.id} className="relative group aspect-square bg-slate-200 bg-cover bg-center rounded overflow-hidden" style={{ backgroundImage: `url(${m.thumbnail_url || m.url})` }} data-testid={`media-${m.id}`}>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex flex-col justify-end p-2 gap-1 opacity-0 group-hover:opacity-100">
              <Button size="sm" onClick={() => setCover(m.id)} className="bg-white text-slate-900 hover:bg-white/90"><Star className="w-3 h-3 mr-1" /> Set cover</Button>
              <Button size="sm" onClick={() => del(m.id)} className="bg-red-600 hover:bg-red-700"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-8">No media yet. Add your best work.</div>}
      </div>
    </div>
  );
}

// ─── Availability tab ─────────────────────────────────────────────
function AvailabilityTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ start_date: '', end_date: '', notes: '' });
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/vendor/me/marketplace/availability`, AUTH());
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.start_date || !f.end_date) return;
    try {
      await axios.post(`${API}/vendor/me/marketplace/availability`, { ...f, block_type: 'unavailable' }, AUTH());
      setF({ start_date: '', end_date: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    await axios.delete(`${API}/vendor/me/marketplace/availability/${id}`, AUTH());
    load();
  };
  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-2">
        <div className="font-semibold">Block dates (unavailable)</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>From</Label><Input type="date" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })} data-testid="avail-from" /></div>
          <div><Label>To</Label><Input type="date" value={f.end_date} onChange={e => setF({ ...f, end_date: e.target.value })} data-testid="avail-to" /></div>
        </div>
        <div><Label>Notes</Label><Input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="Personal trip, holiday…" /></div>
        <Button onClick={add} className="bg-teal-600 hover:bg-teal-700" data-testid="avail-add-btn">Block dates</Button>
      </Card>
      <div className="space-y-2">
        {items.map(b => (
          <Card key={b.id} className="p-3 flex items-center justify-between" data-testid={`avail-${b.id}`}>
            <div className="text-sm">
              <div className="font-medium">{b.start_date} → {b.end_date}</div>
              {b.notes && <div className="text-xs text-slate-500">{b.notes}</div>}
            </div>
            <Button size="sm" variant="outline" onClick={() => del(b.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-slate-500 py-6">No blocks yet. All dates are available.</div>}
      </div>
      <div className="text-xs text-slate-500">Confirmed bookings also automatically block the corresponding date/time.</div>
    </div>
  );
}

// ─── Bookings tab ─────────────────────────────────────────────────
function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const url = statusFilter ? `${API}/vendor/me/marketplace/bookings?status=${statusFilter}` : `${API}/vendor/me/marketplace/bookings`;
    const r = await axios.get(url, AUTH());
    setItems(r.data); setLoading(false);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {['', 'quote_requested', 'quote_sent', 'pending_payment', 'deposit_paid', 'paid_in_full', 'completed'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs rounded-full ${statusFilter === s ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'}`}
            data-testid={`booking-filter-${s || 'all'}`}
          >{s ? s.replace(/_/g, ' ') : 'All'}</button>
        ))}
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : items.length === 0 ? (
        <div className="text-center text-slate-500 py-8">No bookings yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map(b => (
            <Card key={b.id} className="p-3 flex justify-between items-center cursor-pointer hover:shadow-md" onClick={() => setDetail(b.id)} data-testid={`vdash-booking-${b.id}`}>
              <div>
                <div className="font-medium">{b.guest_name || b.guest_email || 'Guest'} · {new Date(b.event_start_at).toLocaleDateString()}</div>
                <div className="text-xs text-slate-500">{b.status.replace(/_/g, ' ')} · {fmt(b.amount_paid)} / {fmt(b.total_amount)}</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{b.status.replace(/_/g, ' ')}</span>
            </Card>
          ))}
        </div>
      )}
      {detail && <BookingDetail id={detail} onClose={() => setDetail(null)} reload={load} />}
    </div>
  );
}

function BookingDetail({ id, onClose, reload }) {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState({ total_amount: '', message: '' });
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/vendor/me/marketplace/bookings/${id}`, AUTH());
    setB(r.data);
    if (r.data.total_amount) setQuote(q => ({ ...q, total_amount: r.data.total_amount }));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const done = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/vendor/me/marketplace/bookings/${id}/mark-completed`, {}, AUTH());
      toast.success('Marked completed');
      reload(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/vendor/me/marketplace/bookings/${id}/send-quote`,
        { total_amount: parseFloat(quote.total_amount), message: quote.message }, AUTH());
      toast.success('Quote sent to client');
      reload(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!b) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="booking-detail-modal">
        <div className="font-semibold text-lg">Booking</div>
        <div className="text-sm space-y-1">
          <div><b>Client:</b> {b.guest_name || '—'} ({b.guest_email})</div>
          {b.guest_phone && <div><b>Phone:</b> {b.guest_phone}</div>}
          <div><b>When:</b> {new Date(b.event_start_at).toLocaleString()}</div>
          <div><b>Type:</b> {b.event_type || '—'}</div>
          <div><b>Status:</b> {b.status}</div>
          <div><b>Total:</b> {fmt(b.total_amount)} · Paid: {fmt(b.amount_paid)} · Due: {fmt(b.amount_due)}</div>
        </div>

        {b.status === 'quote_requested' && (
          <Card className="p-3 border-teal-200">
            <div className="text-sm font-medium mb-2">Send quote</div>
            <Label className="text-xs">Total amount ($)</Label>
            <Input type="number" step="0.01" value={quote.total_amount} onChange={e => setQuote({ ...quote, total_amount: e.target.value })} data-testid="quote-amount" />
            <Label className="text-xs mt-2">Message to client</Label>
            <Textarea rows={3} value={quote.message} onChange={e => setQuote({ ...quote, message: e.target.value })} />
            <Button onClick={send} disabled={busy || !quote.total_amount} className="w-full mt-2 bg-teal-600 hover:bg-teal-700" data-testid="send-quote-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send quote'}
            </Button>
          </Card>
        )}

        {b.status === 'paid_in_full' && (
          <Button onClick={done} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="mark-completed-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark completed'}
          </Button>
        )}

        {b.payments?.length > 0 && (
          <div className="pt-2 border-t">
            <div className="text-xs font-semibold uppercase text-slate-500">Payments</div>
            {b.payments.map(p => (
              <div key={p.id} className="text-sm flex justify-between py-1">
                <span>{new Date(p.paid_at).toLocaleDateString()}{p.is_deposit ? ' (deposit)' : ''}</span>
                <span className="font-medium">{fmt(p.amount)}</span>
              </div>
            ))}
          </div>
        )}

        <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
      </Card>
    </div>
  );
}

// ─── Share tab ────────────────────────────────────────────────────
function ShareTab({ slug }) {
  const [links, setLinks] = useState([]);
  const [f, setF] = useState({ channel: 'instagram', label: '' });
  const publicUrl = `https://app.ievntur.com/vendor/${slug}`;
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/vendor/me/marketplace/share-links`, AUTH());
    setLinks(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await axios.post(`${API}/vendor/me/marketplace/share-links`, f, AUTH());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success('Copied'); };

  const qrSrc = useMemo(() => `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicUrl)}`, [publicUrl]);

  return (
    <div className="space-y-4">
      <Card className="p-4 flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <img alt="QR" src={qrSrc} className="w-32 h-32 rounded border" data-testid="share-qr" />
        <div className="flex-1 min-w-0">
          <Label>Your public page</Label>
          <div className="flex gap-2 mt-1">
            <Input readOnly value={publicUrl} data-testid="share-public-url" />
            <Button onClick={() => copy(publicUrl)} variant="outline"><Copy className="w-4 h-4" /></Button>
          </div>
          <div className="text-xs text-slate-500 mt-2">Share this URL or QR code anywhere — no login required for guests to book.</div>
        </div>
      </Card>

      <Card className="p-4 space-y-2">
        <div className="font-semibold">Tracked short links</div>
        <div className="grid grid-cols-3 gap-2">
          <select className="border rounded-md px-3 py-2 text-sm" value={f.channel} onChange={e => setF({ ...f, channel: e.target.value })}>
            <option>instagram</option><option>tiktok</option><option>facebook</option>
            <option>twitter</option><option>tv-ad</option><option>email</option>
            <option>flyer</option><option>qr</option><option>other</option>
          </select>
          <Input value={f.label} onChange={e => setF({ ...f, label: e.target.value })} placeholder="Label (optional)" className="col-span-1" />
          <Button onClick={create} className="bg-teal-600 hover:bg-teal-700" data-testid="create-shortlink-btn"><Plus className="w-4 h-4 mr-1" /> Create</Button>
        </div>
        <div className="space-y-2 pt-2">
          {links.map(l => (
            <div key={l.id} className="flex items-center gap-2 justify-between text-sm border rounded p-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">app.ievntur.com/s/{l.short_slug}</div>
                <div className="text-xs text-slate-500">{l.channel || '—'} · {l.click_count} clicks · {l.label || 'no label'}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => copy(`https://app.ievntur.com/s/${l.short_slug}`)}><Copy className="w-3 h-3" /></Button>
            </div>
          ))}
          {links.length === 0 && <div className="text-slate-500 text-sm">No short links yet.</div>}
        </div>
      </Card>
    </div>
  );
}
